// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode, type RefObject } from 'react';

import type { GeometriEditor, GestureStatus } from '../../app/bygning/GeometriEditorContext';
import {
  annotationsAt,
  chainOffsetTo,
  guideGripAt,
  moveChainPoint,
  moveGuideLine,
  moveGuidePoint,
  pickClick,
  removeChainPoint,
} from '../../bygning/annotationEdit';
import { dimGeometry, guideSnaps, onStorey, type DimChain, type DimGeometry, type Guide, type GuideLine } from '../../bygning/cadMeasure';
import { lengthInput } from '../../bygning/geometriEdit';
import { INFERENCE_TOKEN, infer, manhattanAngle, type Inference, type SnapSegment } from '../../bygning/inference';
import type { XY } from '../../bygning/planWalls';
import type { ViewportHandle } from '../../viewport/Viewport';
import { Glyph, ScreenTip } from './GeometriLayer';
import geo from './GeometriLayer.module.css';
import styles from './MeasureToolLayer.module.css';

/** Pick slack around a guide or chain, px. */
const PICK_PX = 8;
/** The inference engine's reach, px. */
const SNAP_PX = 12;
/** A press that moves less than this (px) is a click. */
const DRAG_PX = 4;
/** In 3D a guide line is drawn this far (m) each way, in pieces: an end behind the camera would not project. */
const REACH_3D = 60;
const PIECES_3D = 120;

export interface AnnotationLayerProps {
  viewport: RefObject<ViewportHandle | null>;
  host: RefObject<HTMLElement | null>;
  editor: GeometriEditor;
  storeyId: number | null;
  /** World z the guides and chains lie at (the storey floor). */
  z: number;
  /** The locked plan is shown (else 3D: this layer takes its own clicks on them). */
  plan: boolean;
}

type GripKind = 'offset' | 'point' | 'guide';

/** A grip drag on a guide or chain: from the press (or the click that armed it) to the commit. */
interface Drag {
  grip: GripKind;
  /** The chain's point (grip 'point'). */
  index: number;
  orig: DimChain | Guide;
  result: DimChain | Guide;
  /** A click without a drag armed it: it follows the pointer until the next click. */
  armed: boolean;
  moved: boolean;
  raw: XY | null;
  alt: boolean;
  inf: Inference | null;
  typed: string | null;
  /** How far a guide moved (signed for a line). */
  shift: number;
}

const fmt = (v: number) => lengthInput(v);
const signed = (v: number) => `${v < 0 ? '−' : ''}${fmt(Math.abs(v))}`;
const isGuide = (x: DimChain | Guide): x is Guide => 'kind' in x;

/**
 * Rediger › Geometri's guides and dimension chains as drawn and edited
 * (frontend-u0o.11, S9) — on the locked plan and in 3D, at the storey's
 * floor:
 *
 *  - guides dashed and quiet, guide points as crosses, chains with witness
 *    lines, ticks and a figure per stretch; under the pointer in Vælg one
 *    lights up, a selected one is drawn in the accent;
 *  - in 3D a click on one selects it (Shift adds); on the plan the Vælg
 *    pointer (GeometriLayer) picks them among the elements, box included;
 *  - one selected shows its grips: a chain's line (drag it, or type the
 *    offset) and each of its points (drag; ⌫ while held removes it), a
 *    guide's (drag; a typed distance moves a line that far square to
 *    itself, a point that far towards the pointer). Every edit is one ⌘Z
 *    step (`cadAnnotations.ts` history, `editJournal.ts`).
 *
 * The guides and chains live in this browser (`cadAnnotations.ts`): a
 * viewer may edit them too.
 */
export function AnnotationLayer({ viewport, host, editor, storeyId, z, plan }: AnnotationLayerProps) {
  const [, setTick] = useState(0);
  const layerRef = useRef<HTMLDivElement>(null);
  const [hot, setHot] = useState<string | null>(null);
  const [drag, setDragState] = useState<Drag | null>(null);
  const dragRef = useRef<Drag | null>(null);
  const live = useRef({ editor, storeyId, z, plan });
  live.current = { editor, storeyId, z, plan };

  // ------------------------------------------------------- projection --
  useEffect(() => {
    let frameId: number | null = null;
    const bump = () => {
      if (frameId !== null) return;
      frameId = requestAnimationFrame(() => {
        frameId = null;
        setTick((t) => t + 1);
      });
    };
    const off = viewport.current?.onCameraChange(bump) ?? (() => {});
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(bump);
    if (layerRef.current) observer?.observe(layerRef.current);
    bump();
    return () => {
      off();
      observer?.disconnect();
      if (frameId !== null) cancelAnimationFrame(frameId);
    };
    // Plan ⇄ 3D swaps the camera without a controls change event: project afresh.
  }, [viewport, plan]);

  const toScreen = useCallback(
    (p: XY): [number, number] | null => {
      const s = viewport.current?.projectWorld(new Float64Array([p[0], p[1], live.current.z]), { clip: false });
      return s && Number.isFinite(s[0]) && Number.isFinite(s[1]) ? [s[0], s[1]] : null;
    },
    [viewport],
  );
  /** The plan point under canvas pixel (x, y), and how many metres `px` pixels span there. */
  const planAt = useCallback(
    (x: number, y: number, px: number): { p: XY; per: number } | null => {
      const a = viewport.current?.planPoint(x, y, live.current.z);
      if (!a) return null;
      const b = viewport.current?.planPoint(x + px, y, live.current.z);
      return { p: [a[0], a[1]], per: b ? Math.hypot(b[0] - a[0], b[1] - a[1]) : 0.1 };
    },
    [viewport],
  );
  const canvasXY = (clientX: number, clientY: number): [number, number] | null => {
    const r = layerRef.current?.getBoundingClientRect();
    return r ? [clientX - r.left, clientY - r.top] : null;
  };

  // -------------------------------------------------- inference input --
  const model = editor.model;
  const snap = editor.snapTargets;
  const segments = useMemo<SnapSegment[]>(() => {
    const out: SnapSegment[] = [];
    for (const w of model.walls ?? []) {
      if (storeyId !== null && w.storey_id !== storeyId) continue;
      out.push({ a: w.start, b: w.end, source: 'wall', label: `Væg ${w.id}`, guid: w.guid });
    }
    for (const t of snap?.plane_traces ?? []) out.push({ a: t.start, b: t.end, source: 'trace', label: `P-${t.plane_label}`, points: t.point_count });
    return out;
  }, [model.walls, snap, storeyId]);
  const axisAngle = useMemo(() => manhattanAngle(snap?.axis_angles, segments), [snap, segments]);
  const inputs = useRef({ segments, axisAngle });
  inputs.current = { segments, axisAngle };

  // ------------------------------------------------------------- drag --
  const evaluate = useCallback(
    (d: Drag, at: { x: number; y: number } | null): Drag => {
      const { editor: ed } = live.current;
      const hit = at ? planAt(at.x, at.y, SNAP_PX) : null;
      let raw = hit?.p ?? d.raw;
      let inf = d.inf;
      if (hit && d.grip !== 'offset') {
        // The guide being moved is no snap target for itself.
        const g = guideSnaps(
          ed.annotations.guides.filter((x) => x.id !== d.orig.id),
          live.current.storeyId,
        );
        inf = infer(hit.p, {
          segments: [...inputs.current.segments, ...g.segments],
          points: g.points,
          axisAngle: inputs.current.axisAngle,
          origin: null,
          toggles: ed.snap,
          tolerance: hit.per,
          suspended: d.alt,
        });
        raw = hit.p;
      }
      const target = inf && d.grip !== 'offset' ? inf.point : raw;
      if (!target) return { ...d, raw, inf };
      const o = d.orig;
      if (!isGuide(o)) {
        const result = d.grip === 'offset' ? chainOffsetTo(o, target, d.typed) : moveChainPoint(o, d.index, target);
        return { ...d, raw, inf, result };
      }
      const moved = o.kind === 'line' ? moveGuideLine(o, target, d.typed) : moveGuidePoint(o, target, d.typed);
      return { ...d, raw, inf, result: moved.guide, shift: moved.moved };
    },
    [planAt],
  );

  const status = (d: Drag): GestureStatus => {
    const keys: { key: string; text: string }[] = [];
    let lead = '';
    let readout = '';
    if (!isGuide(d.result)) {
      const g = dimGeometry(d.result);
      if (d.grip === 'offset') {
        lead = 'Målkæde · flyt målelinjen';
        keys.push({ key: '0–9', text: 'skriv afstand' });
        readout = `Afstand ${fmt(Math.abs(d.result.offset))} m`;
      } else {
        lead = `Målkæde · flyt punkt ${d.index + 1}`;
        keys.push({ key: '⌫', text: 'fjern punktet' }, { key: 'Alt', text: 'uden snap' });
        readout = `I alt ${fmt(g.total)} m`;
      }
    } else {
      lead = d.result.kind === 'line' ? 'Hjælpelinje · flyt' : 'Hjælpepunkt · flyt';
      keys.push({ key: '0–9', text: 'skriv afstand' }, { key: 'Alt', text: 'uden snap' });
      readout = `Flyttet ${d.result.kind === 'line' ? signed(d.shift) : fmt(d.shift)} m`;
    }
    keys.push({ key: '↵', text: 'sæt' }, { key: 'Esc', text: 'annullér' });
    return { hint: { lead: d.armed ? `${lead} · klik for at sætte` : lead, keys }, readout };
  };

  const update = useCallback(
    (d: Drag | null) => {
      dragRef.current = d;
      setDragState(d);
      live.current.editor.gesture.set(d ? status(d) : null);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const finish = useCallback(
    (d: Drag) => {
      update(null);
      if (JSON.stringify(d.result) !== JSON.stringify(d.orig)) live.current.editor.annotate({ type: 'update', item: d.result });
    },
    [update],
  );

  const startGrip = (grip: GripKind, item: DimChain | Guide, index: number, e: ReactPointerEvent) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    const x0 = e.clientX;
    const y0 = e.clientY;
    // Where it was pressed: a typed value before any move measures towards there.
    const at = canvasXY(x0, y0);
    const raw = at ? (planAt(at[0], at[1], 1)?.p ?? null) : null;
    update({ grip, index, orig: item, result: item, armed: false, moved: false, raw, alt: e.altKey, inf: null, typed: null, shift: 0 });
    const move = (m: PointerEvent) => {
      const cur = dragRef.current;
      if (!cur) return;
      const moved = cur.moved || Math.hypot(m.clientX - x0, m.clientY - y0) >= DRAG_PX;
      if (!moved) return;
      const at = canvasXY(m.clientX, m.clientY);
      update(evaluate({ ...cur, moved, alt: m.altKey }, at ? { x: at[0], y: at[1] } : null));
    };
    const end = () => {
      window.removeEventListener('pointermove', move, true);
      window.removeEventListener('pointerup', up, true);
      window.removeEventListener('pointercancel', cancel, true);
    };
    const up = () => {
      const cur = dragRef.current;
      if (!cur) return end();
      if (!cur.moved || cur.typed) {
        // A click arms the grip: it follows the pointer until the next click or Enter (SketchUp).
        window.removeEventListener('pointerup', up, true);
        update({ ...cur, armed: true });
        return;
      }
      end();
      finish(cur);
    };
    const cancel = () => {
      end();
      update(null);
    };
    window.addEventListener('pointermove', move, true);
    window.addEventListener('pointerup', up, true);
    window.addEventListener('pointercancel', cancel, true);
  };

  // Keys while a drag lasts: the typed distance, ⌫ (a chain's point goes), Enter, Esc.
  const dragOn = drag !== null;
  useEffect(() => {
    if (!dragOn) return;
    const stop = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };
    const onKey = (e: KeyboardEvent) => {
      const d = dragRef.current;
      if (!d) return;
      if (e.key === 'Escape') {
        stop(e);
        if (d.typed) update(evaluate({ ...d, typed: null }, null));
        else update(null);
        return;
      }
      if (e.key === 'Enter') {
        stop(e);
        finish(d);
        return;
      }
      if (e.key === 'Backspace' || e.key === 'Delete') {
        stop(e);
        if (d.typed) {
          update(evaluate({ ...d, typed: d.typed.slice(0, -1) || null }, null));
          return;
        }
        if (d.grip === 'point' && !isGuide(d.orig)) {
          const without = removeChainPoint(d.orig, d.index);
          update(null);
          if (without) live.current.editor.annotate({ type: 'update', item: without });
          else live.current.editor.notify('En målkæde har mindst to punkter — slet hele kæden med Slet');
        }
        return;
      }
      if (/^[0-9.,]$/.test(e.key) && !e.metaKey && !e.ctrlKey && d.grip !== 'point') {
        stop(e);
        update(evaluate({ ...d, armed: true, typed: (d.typed ?? '') + e.key }, null));
        return;
      }
      if (e.key === 'Alt') update(evaluate({ ...d, alt: true }, null));
      // Every other letter waits: no tool switch mid-drag.
      if (e.key.length === 1) stop(e);
    };
    const onKeyUp = (e: KeyboardEvent) => {
      const d = dragRef.current;
      if (d && e.key === 'Alt') update(evaluate({ ...d, alt: false }, null));
    };
    // An armed grip follows the pointer with no button held; the next click sets it.
    const onMove = (m: PointerEvent) => {
      const d = dragRef.current;
      if (!d || !d.armed || m.buttons !== 0) return;
      const at = canvasXY(m.clientX, m.clientY);
      update(evaluate({ ...d, moved: true, alt: m.altKey }, at ? { x: at[0], y: at[1] } : null));
    };
    const onDown = (m: PointerEvent) => {
      const d = dragRef.current;
      if (!d?.armed || m.button !== 0) return;
      m.stopPropagation();
      m.preventDefault();
      finish(d);
    };
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('keyup', onKeyUp, true);
    window.addEventListener('pointermove', onMove, true);
    window.addEventListener('pointerdown', onDown, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('keyup', onKeyUp, true);
      window.removeEventListener('pointermove', onMove, true);
      window.removeEventListener('pointerdown', onDown, true);
    };
  }, [dragOn, evaluate, finish, update]);

  // ---------------------------------------- hover, and 3D's own picks --
  const picking = editor.active && editor.tool === 'select' && !editor.modify;
  useEffect(() => {
    const el = host.current;
    if (!el || !picking) {
      setHot(null);
      return;
    }
    let cursorSet = false;
    const hitAt = (e: PointerEvent): string | null => {
      if (!(e.target instanceof HTMLCanvasElement) || dragRef.current) return null;
      const at = canvasXY(e.clientX, e.clientY);
      const hit = at ? planAt(at[0], at[1], PICK_PX) : null;
      if (!hit) return null;
      const { editor: ed, storeyId: sid } = live.current;
      return annotationsAt(ed.annotations, hit.p, hit.per, sid)[0] ?? null;
    };
    const onMove = (e: PointerEvent) => {
      if (e.buttons !== 0) return;
      const id = hitAt(e);
      setHot(id);
      // Show that a click picks here (the plan's and the camera's cursor is the default).
      if ((id !== null) !== cursorSet) {
        cursorSet = id !== null;
        el.style.cursor = cursorSet ? 'pointer' : '';
      }
    };
    const onLeave = () => setHot(null);
    let pressed: { x: number; y: number } | null = null;
    const onDown = (e: PointerEvent) => {
      // On the plan the Vælg pointer picks them among the elements (GeometriLayer).
      if (live.current.plan || e.button !== 0) return;
      const id = hitAt(e);
      if (id === null) {
        pressed = { x: e.clientX, y: e.clientY };
        return;
      }
      // Ours, not the camera's or the element pick's.
      e.stopPropagation();
      e.preventDefault();
      const ed = live.current.editor;
      const next = pickClick({ elements: ed.selection, annotations: ed.annotationSelection }, { kind: 'annotation', id }, e.shiftKey);
      ed.select(next.elements, next.annotations);
    };
    const onUp = (e: PointerEvent) => {
      const p = pressed;
      pressed = null;
      // A plain click on nothing of ours lets the guides and chains go (the element pick does the rest).
      if (!p || e.shiftKey || Math.hypot(e.clientX - p.x, e.clientY - p.y) > DRAG_PX) return;
      const ed = live.current.editor;
      if (ed.annotationSelection.length > 0) ed.selectAnnotations([], true);
    };
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerleave', onLeave);
    el.addEventListener('pointerdown', onDown, true);
    el.addEventListener('pointerup', onUp);
    return () => {
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerleave', onLeave);
      el.removeEventListener('pointerdown', onDown, true);
      el.removeEventListener('pointerup', onUp);
      if (cursorSet) el.style.cursor = '';
    };
  }, [host, picking, planAt]);

  // A selection change, another tool or leaving Geometri drops a drag.
  const selected = editor.annotationSelection;
  const single = selected.length === 1 && editor.selection.length === 0 ? selected[0] : null;
  useEffect(() => {
    if (dragRef.current && dragRef.current.orig.id !== single) update(null);
  }, [single, update]);
  useEffect(() => {
    if (!picking && dragRef.current) update(null);
  }, [picking, update]);
  useEffect(() => () => live.current.editor.gesture.set(null), []);

  // ------------------------------------------------------------- draw --
  const poly = (pts: XY[]): string | null => {
    const out: string[] = [];
    for (const p of pts) {
      const s = toScreen(p);
      if (!s) return null;
      out.push(`${s[0].toFixed(1)},${s[1].toFixed(1)}`);
    }
    return out.join(' ');
  };
  /** A guide line on screen: two ends on the plan; in 3D the pieces in front of the camera. */
  const guidePieces = (g: Pick<GuideLine, 'a' | 'dir'>): string[] => {
    if (plan) {
      const d = poly([
        [g.a[0] - g.dir[0] * 200, g.a[1] - g.dir[1] * 200],
        [g.a[0] + g.dir[0] * 200, g.a[1] + g.dir[1] * 200],
      ]);
      return d ? [d] : [];
    }
    const out: string[] = [];
    let run: string[] = [];
    for (let i = 0; i <= PIECES_3D; i += 1) {
      const t = -REACH_3D + (2 * REACH_3D * i) / PIECES_3D;
      const s = toScreen([g.a[0] + g.dir[0] * t, g.a[1] + g.dir[1] * t]);
      if (s) run.push(`${s[0].toFixed(1)},${s[1].toFixed(1)}`);
      else {
        if (run.length > 1) out.push(run.join(' '));
        run = [];
      }
    }
    if (run.length > 1) out.push(run.join(' '));
    return out;
  };
  const label = (p: XY, text: string, cls: string, key: string) => {
    const s = toScreen(p);
    if (!s) return null;
    return (
      <span key={key} className={cls} style={{ transform: `translate(${s[0].toFixed(1)}px, ${s[1].toFixed(1)}px) translate(-50%, -50%)` }}>
        {text}
      </span>
    );
  };
  const chainLines = (g: DimGeometry, key: string, cls: string) => {
    const line = poly(g.line);
    return (
      <g key={key} className={cls}>
        {line && <polyline points={line} />}
        {g.witness.map(([a, b], i) => {
          const d = poly([a, b]);
          return d ? <polyline key={`w${i}`} points={d} className={styles.witness} /> : null;
        })}
        {g.ticks.map((t, i) => {
          const s = toScreen(t);
          return s ? <path key={`t${i}`} d={`M${s[0] - 4} ${s[1] + 4}L${s[0] + 4} ${s[1] - 4}`} className={styles.tick} /> : null;
        })}
      </g>
    );
  };

  // The dragged one as it would be.
  const shown = <T extends DimChain | Guide>(x: T): T => (drag && drag.orig.id === x.id ? (drag.result as T) : x);
  const guides = onStorey(editor.annotations.guides, storeyId).map(shown);
  const dims = onStorey(editor.annotations.dims, storeyId).map(shown);
  const sel = new Set(selected);
  const stateCls = (id: string) => (sel.has(id) ? ` ${styles.picked}` : hot === id && picking ? ` ${styles.hot}` : '');

  const target = single ? (guides.find((g) => g.id === single) ?? dims.find((d) => d.id === single) ?? null) : null;
  const gripsOn = picking && target !== null;
  const centre = (() => {
    const el = layerRef.current;
    return el ? planAt(el.clientWidth / 2, el.clientHeight / 2, 1)?.p : null;
  })();

  const inf = drag && drag.moved && !drag.typed && drag.grip !== 'offset' ? drag.inf : null;
  const infAt = inf && inf.kind !== 'free' ? toScreen(inf.point) : null;
  const axisGuide = inf?.guide ? poly([inf.guide[0], inf.guide[1]]) : null;

  const grips: ReactNode[] = [];
  if (gripsOn && target) {
    const gripCls = (on: boolean) => (on ? `${geo.grip} ${geo.gripOn}` : geo.grip);
    if (!isGuide(target)) {
      // The line's grip just past its far end: the middle is where a one-stretch chain's figure stands.
      const g = dimGeometry(target);
      const end = toScreen(g.line[1]);
      const from = toScreen(g.line[0]);
      const run = end && from ? Math.hypot(end[0] - from[0], end[1] - from[1]) : 0;
      const s: [number, number] | null = end && from && run > 1 ? [end[0] + ((end[0] - from[0]) / run) * 18, end[1] + ((end[1] - from[1]) / run) * 18] : end;
      const n: XY = [-target.dir[1], target.dir[0]];
      const up = toScreen([g.line[1][0] + n[0], g.line[1][1] + n[1]]);
      if (s && end) {
        const ux = up ? up[0] - end[0] : 0;
        const uy = up ? up[1] - end[1] : -1;
        const ul = Math.hypot(ux, uy) || 1;
        const ax = (ux / ul) * 16;
        const ay = (uy / ul) * 16;
        grips.push(
          <g key="offset">
            <path className={geo.arrow} d={`M${s[0] + ax * 0.55} ${s[1] + ay * 0.55}L${s[0] + ax} ${s[1] + ay}M${s[0] - ax * 0.55} ${s[1] - ay * 0.55}L${s[0] - ax} ${s[1] - ay}`} />
            <rect
              x={s[0] - 5}
              y={s[1] - 5}
              width={10}
              height={10}
              transform={`rotate(45 ${s[0]} ${s[1]})`}
              className={gripCls(drag?.grip === 'offset')}
              onPointerDown={(e) => startGrip('offset', target, 0, e)}
            >
              <title>Træk målelinjen — eller klik og skriv afstanden</title>
            </rect>
          </g>,
        );
      }
      target.points.forEach((p, i) => {
        const q = toScreen(p);
        if (!q) return;
        grips.push(
          <circle key={`p${i}`} cx={q[0]} cy={q[1]} r={5.5} className={gripCls(drag?.grip === 'point' && drag.index === i)} onPointerDown={(e) => startGrip('point', target, i, e)}>
            <title>Træk punktet · ⌫ mens det holdes fjerner det</title>
          </circle>,
        );
      });
    } else {
      const at = target.kind === 'line' ? (centre ? guideGripAt(target, centre) : target.a) : target.p;
      const s = toScreen(at);
      if (s) {
        grips.push(
          target.kind === 'line' ? (
            <rect
              key="guide"
              x={s[0] - 5}
              y={s[1] - 5}
              width={10}
              height={10}
              transform={`rotate(45 ${s[0]} ${s[1]})`}
              className={gripCls(drag !== null)}
              onPointerDown={(e) => startGrip('guide', target, 0, e)}
            >
              <title>Træk hjælpelinjen — eller klik og skriv hvor langt den skal flyttes</title>
            </rect>
          ) : (
            <rect key="guide" x={s[0] - 5} y={s[1] - 5} width={10} height={10} className={gripCls(drag !== null)} onPointerDown={(e) => startGrip('guide', target, 0, e)}>
              <title>Træk hjælpepunktet — eller klik og skriv hvor langt det skal flyttes</title>
            </rect>
          ),
        );
      }
    }
  }

  const pointerAt = drag?.raw ? toScreen(drag.inf && drag.grip !== 'offset' ? drag.inf.point : drag.raw) : null;
  const boxLive = drag
    ? !isGuide(drag.result)
      ? fmt(Math.abs(drag.result.offset))
      : fmt(Math.abs(drag.shift))
    : '';

  return (
    <div ref={layerRef} className={geo.layer}>
      <svg className={geo.svg} aria-hidden="true">
        {guides.map((g) => {
          const cls = `${g.kind === 'line' ? styles.guideLine : styles.guidePoint}${stateCls(g.id)}`;
          if (g.kind === 'line') {
            return (
              <g key={g.id}>
                {guidePieces(g).map((d, i) => (
                  <polyline key={i} points={d} className={cls} />
                ))}
              </g>
            );
          }
          const s = toScreen(g.p);
          return s ? <path key={g.id} d={`M${s[0] - 6} ${s[1]}H${s[0] + 6}M${s[0]} ${s[1] - 6}V${s[1] + 6}`} className={cls} /> : null;
        })}
        {dims.map((d) => chainLines(dimGeometry(d), d.id, `${styles.chain}${stateCls(d.id)}`))}
        {axisGuide && inf && <polyline points={axisGuide} className={geo.guide} style={{ stroke: `var(${INFERENCE_TOKEN[inf.kind]})` }} />}
        {grips}
        {infAt && inf && <Glyph kind={inf.kind} at={infAt} />}
      </svg>
      {dims.flatMap((d) =>
        dimGeometry(d).stretches.map((s, i) => label(s.at, fmt(s.value), `${styles.chainFigure}${sel.has(d.id) ? ` ${styles.chainFigurePicked}` : ''}`, `${d.id}-${i}`)),
      )}
      {infAt && inf?.tip && <ScreenTip inf={inf} at={infAt} />}
      {drag && drag.moved && pointerAt && drag.grip !== 'point' && (
        <span className={geo.measure} style={{ transform: `translate(${(pointerAt[0] + 16).toFixed(1)}px, ${(pointerAt[1] + 16).toFixed(1)}px)` }} role="status">
          <span className={geo.measureBox}>
            <small>{drag.grip === 'offset' ? 'Afstand' : 'Flyt'}</small>
            <b>
              {drag.typed ? drag.typed : <span className={geo.measureLive}>{boxLive}</span>}
              <u aria-hidden="true" />
              {' m'}
            </b>
          </span>
          <span className={geo.measureCap}>Skriv et tal · ↵ sæt</span>
        </span>
      )}
    </div>
  );
}
